import React, { useEffect, useMemo, useRef, useCallback } from 'react';
import { Checkbox, FormControlLabel, Box } from '@mui/material';

// --- INTERFACES ---
export interface TreeDataItem {
  label: string;
  value: string;
  children?: TreeDataItem[];
}

interface CheckboxTreeNodeProps {
  item: TreeDataItem;
  checkedValues: string[];
  disabledValues: string[];
  disabled?: boolean;
  onCheckChange: (newValues: string[]) => void;
}

const getSubtreeValues = (node: TreeDataItem): string[] => {
  const values: string[] = [node.value];
  if (node.children) {
    node.children.forEach((child) => {
      values.push(...getSubtreeValues(child));
    });
  }
  return values;
};

const getLeafValues = (node: TreeDataItem): string[] => {
  const values: string[] = [];
  const gatherLeaves = (n: TreeDataItem) => {
    if (!n.children || n.children.length === 0) {
      values.push(n.value);
      return;
    }
    n.children.forEach(gatherLeaves);
  };
  gatherLeaves(node);
  return values;
};

const getNonLeafValueSet = (nodes: TreeDataItem[]): Set<string> => {
  const result = new Set<string>();
  const scan = (items: TreeDataItem[]) => {
    items.forEach((item) => {
      if (item.children && item.children.length > 0) {
        result.add(item.value);
        scan(item.children);
      }
    });
  };
  scan(nodes);
  return result;
};

const getFixedFallbackInitValue = (treeData: TreeDataItem[], disabledValues: string[]): string[] => {
  if (!treeData || treeData.length === 0) return [];
  const disabledSet = new Set(disabledValues);

  const hasActiveLeaf = (node: TreeDataItem): boolean => {
    if (!node.children || node.children.length === 0) {
      return !disabledSet.has(node.value);
    }
    return node.children.some(hasActiveLeaf);
  };

  const findFirstActiveNode = (nodes: TreeDataItem[]): TreeDataItem | undefined => {
    for (const node of nodes) {
      if (hasActiveLeaf(node)) return node;
      if (node.children && node.children.length > 0) {
        const found = findFirstActiveNode(node.children);
        if (found) return found;
      }
    }
    return undefined;
  };

  const collectActiveLeafSubtree = (node: TreeDataItem): string[] => {
    if (disabledSet.has(node.value)) return [];
    if (!node.children || node.children.length === 0) return [node.value];

    const result: string[] = [];
    node.children.forEach((child) => {
      result.push(...collectActiveLeafSubtree(child));
    });
    return result;
  };

  const targetNode = findFirstActiveNode(treeData);
  return targetNode ? collectActiveLeafSubtree(targetNode) : [];
};

// --- COMPONENT: CHECKBOX TREE NODE ---
export const CheckboxTreeNode: React.FC<CheckboxTreeNodeProps> = ({
  item,
  checkedValues,
  disabledValues,
  disabled = false,
  onCheckChange,
}) => {
  const hasChildren = !!(item.children && item.children.length > 0);
  const isDisabled = disabled || disabledValues.includes(item.value);

  // Memoize these calculations to prevent recalculation on every parent re-render
  const { isAllChildrenChecked, isIndeterminate, activeLeafSubtreeValues } = useMemo(() => {
    if (!hasChildren) {
      return { isAllChildrenChecked: false, isIndeterminate: false, activeLeafSubtreeValues: [] };
    }
    const leaves = getLeafValues(item);
    const activeLeaves = leaves.filter((v) => !disabledValues.includes(v));

    if (activeLeaves.length === 0) {
      return { isAllChildrenChecked: false, isIndeterminate: false, activeLeafSubtreeValues: activeLeaves };
    }

    const checkedCount = activeLeaves.filter((v) => checkedValues.includes(v)).length;
    const allChecked = checkedCount === activeLeaves.length;
    const someChecked = checkedCount > 0;

    return {
      isAllChildrenChecked: allChecked,
      isIndeterminate: someChecked && !allChecked,
      activeLeafSubtreeValues: activeLeaves,
    };
  }, [item, hasChildren, disabledValues, checkedValues]);

  const isChecked = !hasChildren && checkedValues.includes(item.value);

  const handleToggle = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (isDisabled) return;

    const isActive = e.target.checked;
    let updatedValues: string[];

    const targetValues = hasChildren ? activeLeafSubtreeValues : [item.value];

    if (isActive) {
      updatedValues = Array.from(new Set([...checkedValues, ...targetValues]));
    } else {
      const targetSet = new Set(targetValues);
      updatedValues = checkedValues.filter((val) => !targetSet.has(val));
    }

    onCheckChange(updatedValues);
  };

  return (
    <Box sx={{ ml: 3, display: 'flex', flexDirection: 'column' }}>
      <FormControlLabel
        label={item.label}
        disabled={isDisabled}
        control={
          <Checkbox
            checked={hasChildren ? isAllChildrenChecked : isChecked}
            indeterminate={isIndeterminate}
            onChange={handleToggle}
            size="small"
          />
        }
      />
      {hasChildren &&
        item.children?.map((child) => (
          <CheckboxTreeNode
            key={child.value}
            item={child}
            checkedValues={checkedValues}
            disabledValues={disabledValues}
            disabled={disabled}
            onCheckChange={onCheckChange}
          />
        ))}
    </Box>
  );
};

// --- MAIN COMPONENT: CHECKBOX TREE ---
const normalizeInputValue = (inputValue: unknown): string[] => {
  if (!Array.isArray(inputValue) || inputValue.length === 0) return [];

  if (typeof inputValue[0] === 'string') {
    return inputValue as string[];
  }

  if (typeof inputValue[0] === 'object' && inputValue[0] !== null) {
    return (inputValue as Record<string, unknown>[])
      .map((item) => {
        const firstStringKey = Object.keys(item).find((k) => typeof item[k] === 'string');
        return firstStringKey ? (item[firstStringKey] as string) : null;
      })
      .filter((val): val is string => val !== null);
  }

  return [];
};

type IObjectValue = { [key: string]: string };
type IValues = string[] | Array<IObjectValue>;

interface CheckboxTreeBaseProps {
  treeData: TreeDataItem[];
  currentCondition?: string;
  configMapper?: Record<string, Record<string, { checked?: boolean; disabled?: boolean }>>;
  enableAutoInit?: boolean;
  disabled?: boolean;
}

export function CheckboxTree(
  props: CheckboxTreeBaseProps & { value?: string[]; onChange?: (v: string[]) => void; valueKey?: never },
): React.ReactElement;

export function CheckboxTree<K extends string>(
  props: CheckboxTreeBaseProps & {
    value?: Array<Record<K, string>>;
    onChange?: (v: Array<Record<K, string>>) => void;
    valueKey: K;
  },
): React.ReactElement;

export function CheckboxTree({
  value,
  onChange,
  treeData,
  currentCondition,
  configMapper,
  enableAutoInit = true,
  disabled = false,
  valueKey,
}: CheckboxTreeBaseProps & {
  value?: IValues;
  onChange?: (v: any) => void;
  valueKey?: string;
}): React.ReactElement {
  const normalizeValues = useCallback((values: string[]): string[] => Array.from(new Set(values)).sort(), []);

  const handleChange = useCallback(
    (stringValues: string[]) => {
      if (!onChange) return;
      if (valueKey) {
        onChange(stringValues.map((v) => ({ [valueKey]: v })));
      } else {
        onChange(stringValues);
      }
    },
    [onChange, valueKey],
  );

  const lastAutoInitUncontrolled = useRef<string[]>([]);
  const prevConditionRef = useRef<string | undefined>(undefined);
  const hasAutoInitTriggeredRef = useRef<boolean>(false);

  const nonLeafValueSet = useMemo(() => getNonLeafValueSet(treeData), [treeData]);
  const normalizedInputValue = useMemo(() => normalizeInputValue(value), [value]);

  const isControlled = value !== undefined;
  const checkedValues = isControlled ? normalizedInputValue : [];

  const areValuesEqual = (left: string[], right: string[]): boolean => {
    if (left.length !== right.length) return false;
    return left.every((item, index) => item === right[index]);
  };

  const stripNonLeafValues = useCallback(
    (values: string[]): string[] => values.filter((v) => !nonLeafValueSet.has(v)),
    [nonLeafValueSet],
  );

  const disabledValues = useMemo(() => {
    if (!currentCondition || !configMapper || !configMapper[currentCondition] || treeData.length === 0) {
      return [];
    }

    const currentConfig = configMapper[currentCondition];
    const disabledSet = new Set<string>();

    const processNodeDisabled = (node: TreeDataItem): boolean => {
      const isSelfDisabled = currentConfig[node.value]?.disabled === true;

      if (!node.children || node.children.length === 0) {
        if (isSelfDisabled) disabledSet.add(node.value);
        return isSelfDisabled;
      }

      const areAllChildrenDisabled = node.children.every(processNodeDisabled);
      const isFinalDisabled = isSelfDisabled || areAllChildrenDisabled;

      if (isFinalDisabled) disabledSet.add(node.value);
      return isFinalDisabled;
    };

    treeData.forEach(processNodeDisabled);
    return Array.from(disabledSet);
  }, [treeData, currentCondition, configMapper]);

  useEffect(() => {
    if (treeData.length === 0 || !onChange) return;

    if (disabled) {
      prevConditionRef.current = currentCondition;
      return;
    }

    const conditionChanged = prevConditionRef.current !== currentCondition;
    const isFirstTime = !hasAutoInitTriggeredRef.current;

    prevConditionRef.current = currentCondition;

    if (isFirstTime && isControlled && normalizedInputValue.length > 0) {
      hasAutoInitTriggeredRef.current = true;
      return;
    }

    if (!enableAutoInit && !conditionChanged) {
      hasAutoInitTriggeredRef.current = true;
      return;
    }

    if (!conditionChanged && hasAutoInitTriggeredRef.current) {
      return;
    }

    const disabledSet = new Set(disabledValues);
    let nextCheckedValues: string[] = [];
    let isTargetConditionMatched = false;

    if (currentCondition && configMapper?.[currentCondition]) {
      isTargetConditionMatched = true;
      const currentConfig = configMapper[currentCondition];

      const checkedSet = new Set<string>();

      const scanChecked = (nodes: TreeDataItem[]) => {
        nodes.forEach((node) => {
          const configItem = currentConfig[node.value];
          if (configItem?.checked === true) {
            checkedSet.add(node.value);
          }
          if (node.children) scanChecked(node.children);
        });
      };

      scanChecked(treeData);
      nextCheckedValues = Array.from(checkedSet);

      const hasValidValue = nextCheckedValues.some((v) => !disabledSet.has(v));
      if (!hasValidValue) {
        nextCheckedValues = getFixedFallbackInitValue(treeData, disabledValues);
      }
    } else {
      nextCheckedValues = getFixedFallbackInitValue(treeData, disabledValues);
    }

    const finalFilteredValues = nextCheckedValues.filter(
      (val) => !nonLeafValueSet.has(val) && (!isTargetConditionMatched || !disabledSet.has(val)),
    );

    const normalizedNext = normalizeValues(finalFilteredValues);
    const normalizedCurrent = normalizeValues(stripNonLeafValues(checkedValues));

    hasAutoInitTriggeredRef.current = true;

    if (!areValuesEqual(normalizedNext, normalizedCurrent)) {
      handleChange(normalizedNext);
      if (!isControlled) {
        lastAutoInitUncontrolled.current = normalizedNext;
      }
    }
  }, [
    treeData,
    currentCondition,
    configMapper,
    disabledValues,
    handleChange,
    isControlled,
    normalizedInputValue,
    enableAutoInit,
    disabled,
    nonLeafValueSet,
    normalizeValues,
    stripNonLeafValues,
  ]);

  return (
    <Box>
      {treeData.map((item) => (
        <CheckboxTreeNode
          key={item.value}
          item={item}
          checkedValues={stripNonLeafValues(checkedValues)}
          disabledValues={disabledValues}
          disabled={disabled}
          onCheckChange={handleChange}
        />
      ))}
    </Box>
  );
}
